import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import type { AiChatReference } from '~/components/AiChat/types';
import type { ClaimCliveReferencesResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

export type ReferencesMap = Record<string, AiChatReference>;

/** Resolves cited external ids to references for one claim. Injectable for stories and tests. */
export type ReferencesResolver = (claimId: number, ids: string[]) => Promise<ReferencesMap>;

// The server caps a resolve call at the same count; a burst citing more is split, not refused.
export const MAX_IDS_PER_REQUEST = 100;

export const fetchClaimReferences: ReferencesResolver = async (claimId, ids) => {
  const { data } = await axios.get<ClaimCliveReferencesResponse>(`/api/v1/claims/${claimId}/clive/references`, {
    params: { ids: ids.join(',') },
  });
  return data.references;
};

interface ClaimReferencesContextValue {
  /** Everything resolved so far on this claim. */
  references: ReferencesMap;
  /** Ids the server answered without — they do not resolve on this claim. */
  misses: ReadonlySet<string>;
  /** Ask for ids; a no-op for any id already asked for, whatever its outcome. */
  request: (ids: string[]) => void;
}

const ClaimReferencesContext = createContext<ClaimReferencesContextValue | undefined>(undefined);

interface ClaimReferencesProviderProps {
  claimId: number;
  resolver?: ReferencesResolver;
  children: React.ReactNode;
}

/**
 * One in-memory cache of the sources this claim's Clive text cites, for every surface that
 * renders such text: the conductor log's narration and action entries, and the activity panel.
 *
 * The conductor's stream reaches the browser straight from Firestore, and an action's explanation
 * comes off the action row, so no server read resolves their citations on the way out. The text
 * asks here. A Firestore snapshot lands a burst of entries in one render, so requests are
 * collected and flushed in a microtask: eighty entries citing the same handful of sources cost
 * one request carrying the union of their ids. A resolved id is never asked for again, and
 * neither is an id whose request failed, because re-asking on every render is a loop. Only an id
 * the server answered without is a miss; one a failed request covered stays pending, since
 * the lookup failing says nothing about the source.
 *
 * The cache belongs to one claim: a new `claimId` remounts it empty, and a request still in flight
 * for the previous claim lands on the unmounted cache and is dropped.
 */
export const ClaimReferencesProvider: React.FC<ClaimReferencesProviderProps> = (props) => (
  <ClaimReferencesCache key={props.claimId} {...props} />
);

const ClaimReferencesCache: React.FC<ClaimReferencesProviderProps> = ({
  claimId,
  resolver = fetchClaimReferences,
  children,
}) => {
  const [references, setReferences] = useState<ReferencesMap>({});
  const [misses, setMisses] = useState<ReadonlySet<string>>(() => new Set());
  const seen = useRef(new Set<string>());
  const queued = useRef(new Set<string>());
  const flushScheduled = useRef(false);

  const flush = useCallback(async () => {
    flushScheduled.current = false;
    const ids = Array.from(queued.current);
    queued.current.clear();
    if (!ids.length) return;

    for (let start = 0; start < ids.length; start += MAX_IDS_PER_REQUEST) {
      const chunk = ids.slice(start, start + MAX_IDS_PER_REQUEST);
      let resolved: ReferencesMap;
      try {
        resolved = await resolver(claimId, chunk);
      } catch (error) {
        await reportAxiosError(error);
        continue;
      }
      const unresolved = chunk.filter((id) => !resolved[id]);
      setReferences((prev) => ({ ...prev, ...resolved }));
      if (unresolved.length) {
        setMisses((prev) => new Set([...prev, ...unresolved]));
      }
    }
  }, [claimId, resolver]);

  const request = useCallback(
    (ids: string[]) => {
      ids.forEach((id) => {
        if (seen.current.has(id)) return;
        seen.current.add(id);
        queued.current.add(id);
      });
      if (queued.current.size && !flushScheduled.current) {
        flushScheduled.current = true;
        queueMicrotask(() => {
          void flush();
        });
      }
    },
    [flush]
  );

  const value = useMemo(() => ({ references, misses, request }), [references, misses, request]);

  return <ClaimReferencesContext.Provider value={value}>{children}</ClaimReferencesContext.Provider>;
};

interface ClaimReferenceLookup {
  /** The source, once resolved. */
  reference?: AiChatReference;
  /** True while the cache has not answered for this id yet — the citation waits, it is not missing. */
  isPending: boolean;
}

/**
 * The source one citation names, asking the cache for it on first sight.
 *
 * Resolution happens at the citation rather than over the whole text: every link asks for its own
 * id, and the provider's microtask flush coalesces a render's worth of asks into one request. A
 * surface with no provider above it gets nothing and nothing pending, so its citations resolve
 * from the caller's map alone, as they did before this existed.
 */
export const useClaimReference = (documentSourceId?: string): ClaimReferenceLookup => {
  const context = useContext(ClaimReferencesContext);

  useEffect(() => {
    if (context && documentSourceId) {
      context.request([documentSourceId]);
    }
  }, [context, documentSourceId]);

  if (!context || !documentSourceId) {
    return { isPending: false };
  }

  const reference = context.references[documentSourceId];

  return { reference, isPending: !reference && !context.misses.has(documentSourceId) };
};

/** Whether a cache is mounted above — what tells the markdown display a citation can be resolved. */
export const useHasClaimReferences = (): boolean => useContext(ClaimReferencesContext) !== undefined;
